import { fetchFavoritesWithExperience } from 'flux/clients/favorites';
import React, { createContext, useEffect, useReducer } from 'react';
import { useUser } from '~/authentication/store';
import { useCurrencyContext } from '~/context/CurrencyContext';

type ACTION_TYPE =
  | 'FETCHING'
  | 'FETCHING_SUCCESS'
  | 'FETCHING_ERROR'
  | 'SET'
  | 'SET_EXPERIENCE';

const INITIAL_STATE = {
  favorites: [],
  loading: false,
  dispatch: (action: { type: ACTION_TYPE; payload?: any }) => {},
};

const dataReducer = (
  state: any,
  action: { type: ACTION_TYPE; payload?: any },
) => {
  switch (action.type) {
    case 'FETCHING':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHING_SUCCESS':
      return {
        ...state,
        loading: false,
        favorites: action.payload.favorites,
      };
    case 'FETCHING_ERROR':
      return {
        ...state,
        loading: false,
      };
    case 'SET':
      return {
        ...state,
        favorites: action.payload.favorites,
      };
    case 'SET_EXPERIENCE':
      return {
        ...state,
        favorites: state.favorites.map((favorite) => {
          if (favorite.id === action.payload.experience.id) {
            return action.payload.experience;
          }
          return favorite;
        }),
      };
    default:
      throw new Error('No such action');
  }
};

const FavoritesContext = createContext(INITIAL_STATE);

const FavoritesContextProvider = ({ children }) => {
  const { isLoggedIn } = useUser();
  const [state, dispatch] = useReducer(dataReducer, INITIAL_STATE);
  const { state: currencyState } = useCurrencyContext();

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: 'FETCHING' });
      fetchFavoritesWithExperience(currencyState.currency)
        .then(({ data, relations }) => {
          const favorites = data
            .sort((a, b) => a.order - b.order)
            .map(({ id }) =>
              relations.find(
                (relation) =>
                  relation.id === id && relation._type === 'experience',
              ),
            )
            .filter((exp) => !!exp);
          dispatch({ type: 'FETCHING_SUCCESS', payload: { favorites } });
        })
        .catch(() => {
          dispatch({ type: 'FETCHING_ERROR' });
        });
    }
  }, [isLoggedIn, currencyState.currency]);

  return (
    <FavoritesContext.Provider value={{ ...state, dispatch }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export { FavoritesContext, FavoritesContextProvider };
